import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AppHeader } from "@/components/Layout/AppHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Flame, Trophy, BarChart3, Award, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getBadgeById } from "@/lib/badges";
import { BadgeIcon } from "@/components/Gamification/BadgeIcon";
import { ProfileEditDialog } from "@/components/Profile/ProfileEditDialog";
import { useHapticFeedback } from "@/hooks/useHapticFeedback";
interface UserProgress {
  xp: number;
  level: number;
  current_streak: number;
  longest_streak: number;
  total_expenses_logged: number;
  last_activity_date: string;
}

interface UserBadge {
  badge_id: string;
  earned_at: string;
}

const Profile = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { light } = useHapticFeedback();

  const handleViewBadges = () => {
    light();
    navigate('/badges');
  };
  const [user, setUser] = useState<any>(null);
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [loading, setLoading] = useState(true);
  const [budget, setBudget] = useState(1000);
  const [currency, setCurrency] = useState('EUR');

  const loadUserData = useCallback(async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      setUser(currentUser);

      // Load budget settings
      const { data: budgetData } = await supabase
        .from('budget_settings')
        .select('monthly_budget, currency')
        .eq('user_id', currentUser.id)
        .maybeSingle();

      if (budgetData) {
        setBudget(budgetData.monthly_budget);
        setCurrency(budgetData.currency);
      }

      // Load progress
      const { data: progressData } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', currentUser.id)
        .maybeSingle();

      if (progressData) {
        setProgress(progressData);
      }

      // Load badges
      const { data: badgesData } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('earned_at', { ascending: false });

      if (badgesData) {
        setBadges(badgesData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const userName = localStorage.getItem('userName') || null;

  const handleProfileSave = () => {
    // Reload user data to reflect changes
    loadUserData();
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const xpForNextLevel = (level: number) => level * 100;
  const xpProgress = progress ? (progress.xp % 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <AppHeader title={t('profile.title')} />
        <main className="flex-1 container mx-auto px-4 py-6 space-y-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AppHeader title={t('profile.title')} />
      
      <main className="flex-1 container mx-auto px-6 pt-20 space-y-6 pb-24 overflow-y-auto">
        {/* User Info Card */}
        <Card className="p-6">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarFallback className="text-xl bg-primary text-primary-foreground">
                {userName ? getInitials(userName) : (user?.email ? getInitials(user.email) : '??')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold truncate">
                {userName || user?.email || 'User'}
              </h2>
              {userName && (
                <p className="text-sm text-muted-foreground truncate">
                  {user?.email}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                {t('profile.levelLabel')} {progress?.level || 1} • {progress?.xp || 0} points
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Next: {t('profile.levelLabel')} {(progress?.level || 1) + 1}</span>
              <span className="font-medium">{xpProgress}/100</span>
            </div>
            <Progress value={xpProgress} className="h-3 bg-gradient-to-r" />
            <p className="text-xs text-muted-foreground text-center">
              {100 - xpProgress} points to level up! 🎯
            </p>
          </div>

          {/* Edit Profile Button */}
          <div className="mt-4 flex justify-center">
            <ProfileEditDialog
              initialName={userName || ''}
              initialBudget={budget}
              initialCurrency={currency}
              onSave={handleProfileSave}
            />
          </div>
        </Card>

        {/* Stats Grid */}
        <div className="flex flex-col gap-4">
          <Card className="p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Flame className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {t('profile.days', { count: progress?.current_streak || 0, s: (progress?.current_streak || 0) === 1 ? '' : 's' })}
                </p>
                <p className="text-xs text-muted-foreground">{t('profile.streakLabel')}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Trophy className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {t('profile.days', { count: progress?.longest_streak || 0, s: (progress?.longest_streak || 0) === 1 ? '' : 's' })}
                </p>
                <p className="text-xs text-muted-foreground">{t('profile.longestStreakLabel')}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <BarChart3 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{progress?.total_expenses_logged || 0}</p>
                <p className="text-xs text-muted-foreground">{t('profile.totalExpensesLabel')}</p>
              </div>
            </div>
          </Card>

          <Card 
            className="p-4 hover:shadow-lg transition-shadow cursor-pointer"
            onClick={handleViewBadges}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Award className="w-5 h-5 text-purple-500" />
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold">{badges.length > 0 ? `${badges.length} earned` : 'No badges yet'}</p>
                <p className="text-xs text-muted-foreground">Achievements</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </Card>
        </div>

        {/* Recent Badges */}
        {badges.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Recent Achievements</h3>
              <button 
                onClick={handleViewBadges}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                View All →
              </button>
            </div>
            <div className="space-y-3">
              {badges.slice(0, 5).map((badge) => {
                const details = getBadgeById(badge.badge_id);
                return (
                  <div key={badge.badge_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <BadgeIcon iconName={details.iconName} size="md" />
                      <div>
                        <p className="font-medium">{details.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(badge.earned_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary">Earned</Badge>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </main>
    </div>
  );
};

export default Profile;
